import Switch from '@/components/Switch/Switch';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { getAvatarElementFromPage, getAvatarGroupFromPage } from '@/pages/AdvancedEditor/utils';
import { useAnalytics } from '@/services/analytics';
import { useFlag } from '@/services/featureFlags';
import { MixPanelEvent } from '@/utils/types';
import styled from '@emotion/styled';
import { Divider } from '@mui/material';
import { ElementType } from 'polotno/model/group-model';
import { ControlRow } from '../../../common/ControlRow';
import { BorderColorControl } from './BorderColorControl';
import { BorderWidthControl } from './BorderWidthControl';

const BorderContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const BorderControl = () => {
    const mixpanel = useAnalytics();
    const avatarMaskingBorderEnabled = useFlag('avatar_masking_border_enabled', false);
    const { store } = useAdvancedEditorContext();
    const avatarElement = getAvatarElementFromPage(store.activePage);

    if (!avatarElement) return null;

    if (!avatarMaskingBorderEnabled) return null;

    const borderEnabled = avatarElement.custom?.borderEnabled;

    const handleSwitchChange = (_: any, checked: boolean) => {
        mixpanel?.track('avatar-masking', { event: MixPanelEvent.CLICK, ['avatar-masking-border']: checked ? 'applied' : 'not-applied' });

        if (checked) {
            handleEnable();
        } else {
            handleDisable();
        }
        avatarElement.set({ custom: { ...avatarElement.custom, borderEnabled: checked } });
    };

    const handleEnable = () => {
        const group = getAvatarGroupFromPage(store.activePage);
        const fillCircle = group?.children.find((child: ElementType) => child.name === 'avatar-fill');
        const fillCircleArray = fillCircle ? [fillCircle.id] : [];

        const { width, height, x, y } = avatarElement;
        const centerX = x + width / 2;
        const centerY = y + height / 2;

        const borderCircle = store.activePage?.addElement({
            name: 'avatar-border',
            type: 'figure',
            subType: 'circle',
            x: centerX - width / 2,
            y: centerY - height / 2,
            width,
            height,
            fill: 'transparent',
            opacity: 1,
            stroke: '#000000',
            strokeWidth: 0,
        });

        if (group) {
            store.ungroupElements([group.id]);
        }

        // order is important here
        store.groupElements([...fillCircleArray, avatarElement.id, borderCircle.id]);
    };

    const handleDisable = () => {
        const group = getAvatarGroupFromPage(store.activePage);

        if (group) {
            const elementsToRemove = group.children
                .filter((child: ElementType) => child.name === 'avatar-border')
                .map((child: ElementType) => child.id);

            store.deleteElements(elementsToRemove);
            store.selectElements([group.id]);
        }
    };

    return (
        <>
            <BorderContainer>
                <ControlRow>
                    Border
                    <Switch checked={borderEnabled} onChange={handleSwitchChange} />
                </ControlRow>

                {borderEnabled && (
                    <>
                        <BorderColorControl />

                        <ControlRow>
                            Border width
                            <BorderWidthControl />
                        </ControlRow>
                    </>
                )}
            </BorderContainer>
            <Divider />
        </>
    );
};
